'use client';

import cx from 'classnames';
import { usePathname, useRouter } from 'next/navigation';
import { PropsWithChildren, useMemo } from 'react';

import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { createTabsComponent } from '@flipster/react/design-system';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import segment from '@flipster/utils/segment';
import { HistoryEvent } from '@flipster/utils/segment/events/history';

import { HistoryTabGroup, getHistoryTabList, getTabList } from './constants';
import styles from './historyPage.module.scss';

const Tabs = createTabsComponent<HistoryTabGroup>();
const Choice = createSingleChoice<string>();

interface Props extends PropsWithChildren {
  title: string;
  isWebview?: boolean;
  historyTabGroup: HistoryTabGroup;
}

const HistoryPageLayout = ({ title, children, isWebview, historyTabGroup }: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const localeStripped = '/' + pathname.split('/').slice(2).join('/');

  const isSpotOnly = useCheckSpotOnly();
  const tabList = useMemo(() => getHistoryTabList(isSpotOnly), [isSpotOnly]);
  const subTabList = useMemo(() => getTabList(historyTabGroup, isSpotOnly), [historyTabGroup, isSpotOnly]);

  return (
    <main className={cx(styles.wrapper, { [styles.webview]: isWebview })}>
      {isWebview && (
        <div className={styles.webviewTitleBar}>
          <h1>{title}</h1>
        </div>
      )}
      {!isWebview && (
        <>
          <h1>History</h1>
          <h2>{title}</h2>
          <div className={styles.historyTab}>
            <Tabs
              border
              type='responsive'
              value={historyTabGroup || 'Transaction'}
              onClick={(tab) => {
                // clicking selected tab again should clean all sub-filters.
                // using onChange prop blocks this feature.
                segment.track(HistoryEvent.HistoryTypeClick, { history_type: tab || '' });
                const tabs = getTabList(tab, isSpotOnly);
                router.push(tabs[0].value);
              }}
            >
              {tabList.map((tab) => (
                <Tabs.Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          </div>
          <div className={styles.submenuTab}>
            <Choice value={localeStripped}>
              {subTabList.map((tab) => (
                <Choice.Chip
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  // clicking selected chip again should clean all sub-filters.
                  // using onChange prop on parent (Choice) blocks this feature.
                  onClick={() => router.push(tab.value)}
                />
              ))}
            </Choice>
          </div>
        </>
      )}
      {children}
    </main>
  );
};

export default HistoryPageLayout;
